import { UpgradeOffer } from '@pubstack/common/src/upgrade'
import { cn } from '@pubstack/ui/utils/cn'
import { useAtomValue } from 'jotai'
import { FunctionComponent, ReactNode, useState } from 'react'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { currentScopeState } from '~/state'
import { WithClassName } from '~/types/utils'
import { contactUsUrl } from '~/utils/hubspot'
import { UpgradeChip } from './UpgradeChip'
import { upgradeDialogCopy } from './upgradeDialogCopy'

type Props = WithClassName & {
  /** What is missing. May be empty, when nothing behind the wall has a product name — see `upgradeDialogCopy`. */
  offers: UpgradeOffer[]
  /** Names the thing the reader just tried to open, when the page can be more specific than the offer. */
  context?: ReactNode
}

/**
 * The one upgrade surface in the app: eyebrow, what it unlocks, what that gets you, and the ask.
 *
 * Written to be dropped anywhere — over a locked table, inside a modal opened from the Catalog, as the
 * body of a route a scope cannot reach — so that a publisher meets the same card wherever they run into
 * the same wall. Before this there were three: a padlock panel on the ad unit tabs, a flyout on the
 * Catalog, and a HubSpot form that opened with no explanation at all.
 *
 * Two columns, words then illustration: 614 of text beside a 190 lock, 56 apart. It was a narrow centred
 * column before, which stacked the lock above the pitch and pushed the button below the fold of a modal.
 * Reading left to right and looking right is one glance; reading top to bottom past a 96px padlock is three.
 *
 * The text column is sized so that every benefit fits on one line — the longest of them runs to 66
 * characters, and at 462 the middle one wrapped onto a second line, which turned a scannable list of three
 * into a paragraph of four. They hold that line by refusing to wrap, so a new benefit cannot quietly
 * reintroduce the fold; one that does not fit should be shortened rather than allowed to fold.
 *
 * The left column may shrink — the panel that hosts this on an ad unit tab is as wide as the page, and
 * the page is as wide as the window. The illustration never does: a lock squeezed to 60px is a smudge.
 * Which is why the no-wrap is a container query on the column rather than a flat rule: below the width the
 * longest benefit needs, text that cannot wrap does not stay on one line, it runs out of the card. Wrapping
 * is the better of the two failures, so the narrow column gets it back.
 *
 * No dismiss of its own. Where it is dismissible the modal around it says so; where it stands in for a
 * page there is nothing to dismiss it to.
 *
 * The ask opens the HubSpot form the sidebar's "Contact us" already uses — deliberately static, with no
 * request stored against the scope and nothing for the backend to do.
 *
 * The lock opens on the CTA's hover, and only then: it is the one thing the button does, said a moment
 * before it is pressed. The illustration used to run the same lift on a loop of its own, which spent the
 * gesture on nobody — a lock that keeps opening by itself is decoration, and stops being read after the
 * second cycle. Pointer only, deliberately: the movement is a preview of the click, and there is nothing
 * for it to preview on a surface that has no pointer to make one.
 */
export const UpgradeOfferDialog: FunctionComponent<Props> = ({ className, offers, context }) => {
  const user = useAuthenticatedUser()
  const scope = useAtomValue(currentScopeState)
  const copy = upgradeDialogCopy(offers)
  const [isCtaHovered, setCtaHovered] = useState(false)

  const onContact = () => window.open(contactUsUrl({ email: user.profile.email, scopeName: scope.name }), '_blank', 'noopener,noreferrer')

  return (
    <div className={cn('flex w-full max-w-[860px] items-center gap-14', className)}>
      <div className={'@container flex min-w-0 flex-1 flex-col gap-8'}>
        <div className={'flex flex-col gap-12'}>
          <div className={'flex flex-col gap-2'}>
            {/* The crown, not the padlock, carries the message: the illustration says a door is shut, and
                the chip is what says it opens. Grey here rather than blue — it is the line above the
                headline, and two blue things competing for the first look is one too many. */}
            <UpgradeChip className={'text-slate-grey text-sm/[22px]'}>Upgrade your contract to improve your ad monetization</UpgradeChip>
            <h2 className={'m-0 font-rubik text-xl/[30px] font-medium'}>{copy.title}</h2>
            {context && <p className={'text-slate-grey m-0 text-sm/[22px]'}>{context}</p>}
          </div>
          {copy.benefits.length > 0 && (
            <ul className={'m-0 flex list-none flex-col gap-4 p-0'}>
              {copy.benefits.map((benefit) => (
                <li key={benefit} className={'flex items-center gap-2 text-base/[26px]'}>
                  <Icon name={'check_circled'} width={'24px'} height={'24px'} fill={'var(--color-success)'} className={'shrink-0'} />
                  <span className={'@min-[544px]:whitespace-nowrap'}>{benefit}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* As wide as its own words, centred under them. It ran the full 614 before, which is wider than
            any of the benefits above it and read as a banner rather than as something to press — a button
            the size of a paragraph stops looking like a button. Centred rather than aligned left because it
            is the end of the pitch and not another line of it: off the left edge, it stops being one more
            item in the list the benefits started.

            A size up from the rest of the card, and the `!` is not decoration. `Button` is Emotion, and
            Emotion injects unlayered rules while these utilities live in `@layer utilities` — unlayered
            wins, so a plain `px-8` would lose to the button's own `padding: 9px 11px` and quietly do
            nothing. Padding is also the only honest knob here: the button is `box-sizing: content-box`, so
            a height utility would be added to that padding rather than replace it. 26px of line, 14 above
            and below, 1 of border — 56px tall against the 46 it was. */}
        <Button
          className={'self-center px-8! py-3.5!'}
          variant={'primary'}
          onClick={onContact}
          onMouseEnter={() => setCtaHovered(true)}
          onMouseLeave={() => setCtaHovered(false)}
          trackingLabel={copy.trackingLabel}
        >
          {copy.cta}
        </Button>
      </div>
      {/* `lock-round-unlocked` is what the illustration listens for — see `lock_round.svg`, which owns the
          motion. Handlers rather than a ref: `Button` spreads its props after its own `ref`, so a ref given
          from here would take the place of the one its ripple and icon hover run on. */}
      <Icon name={'lock_round'} width={'190px'} height={'190px'} className={cn('shrink-0', isCtaHovered && 'lock-round-unlocked')} />
    </div>
  )
}
